import React, { useState, useCallback, useMemo, useEffect } from "react";

import { parseEmbodiedNum, useEmbodiedFieldId } from "../embodiedFormUtils";
import { sanitizeSourceInput, USER_DEFINED_LABEL } from "../wallLayerEmbodiedCarbon";
import {
    getUdefLayerEmbCarbonFieldKey,
    getUdefLayerEmbodiedCarbon,
    getUdefFramingAssemblyCalculated,
    getUdefFramingAssemblyEmbC,
    getUdefFramingAssemblySnapshotForEmbC,
    getWallLayersSnapshotForUdefEmbC,
    isCavityInsulationLayer,
} from "utils/enclosure/embc/udefStandardLayers";
import {
    getUdefWindowCodeEmbodiedCarbon,
    getWindowLayersSnapshotForUdefEmbC,
    WINDOW_UDEF_EMB_KEY,
} from "utils/enclosure/embc/udefWindowLayers";

import classes from "./style.module.scss";

// Specifically for User Defined Codes
const EmbodiedCarbonEditor = React.memo(({ change, display, layer = {}, layersFieldPrefix = "", layerType = "" }) => {
    const [draftEmissions, setDraftEmissions] = useState("");
    const [draftStorage, setDraftStorage] = useState("");
    const [draftSource, setDraftSource] = useState("");
    const sourceInputId = useEmbodiedFieldId(`volta-emb-udef-code-source-${layerType}`);

    const isWindowLayer = layerType.includes("window");
    const isFramingAssembly = isCavityInsulationLayer(layerType);

    const defaultRow = useMemo(() => {
        if (isWindowLayer) {
            return getUdefWindowCodeEmbodiedCarbon(layer);
        }
        if (isFramingAssembly) {
            return getUdefFramingAssemblyCalculated({ layerType, layer });
        }
        return getUdefLayerEmbodiedCarbon({ layerType, layer });
    }, [layer, layerType, isWindowLayer, isFramingAssembly]);

    const embKey = isWindowLayer ? WINDOW_UDEF_EMB_KEY : getUdefLayerEmbCarbonFieldKey(layerType);
    const embC = isFramingAssembly ? getUdefFramingAssemblyEmbC(layer, layerType) : layer?.[embKey];
    const embPath = `${layersFieldPrefix}.${embKey}`;
    const legacyCavityEmbPath = isFramingAssembly ? `${layersFieldPrefix}.cavityInsulation.${embKey}` : null;

    const defaultSourceText = defaultRow.source || defaultRow.defaultSource || "";

    useEffect(() => {
        if (embC && embC.emissions != null) {
            setDraftEmissions(String(embC.emissions));
            setDraftStorage(embC.storage != null ? String(embC.storage) : "");
            setDraftSource(
                sanitizeSourceInput(embC.source != null && embC.source !== "" ? embC.source : USER_DEFINED_LABEL)
            );
        } else {
            setDraftEmissions(defaultRow.emissions != null ? String(defaultRow.emissions) : "");
            setDraftStorage(defaultRow.storage != null ? String(defaultRow.storage) : "");
            setDraftSource("");
        }
    }, [embC, defaultRow]);

    const applyEmbC = useCallback(() => {
        if (!change) return;
        const e = parseEmbodiedNum(draftEmissions);
        if (e == null) return;
        const s = parseEmbodiedNum(draftStorage);
        const trimmed = sanitizeSourceInput(draftSource).trim();
        const nextSource = trimmed === "" ? USER_DEFINED_LABEL : trimmed;

        const snapshot = isWindowLayer
            ? getWindowLayersSnapshotForUdefEmbC(layer)
            : isFramingAssembly
              ? getUdefFramingAssemblySnapshotForEmbC({ layerType, layer })
              : getWallLayersSnapshotForUdefEmbC(layer);

        change(embPath, {
            emissions: e,
            storage: s ?? 0,
            source: nextSource,
            ...snapshot,
        });

        // Clear legacy cavity-only override so assembly value is authoritative
        if (legacyCavityEmbPath) {
            change(legacyCavityEmbPath, null);
        }
    }, [change, embPath, legacyCavityEmbPath, draftEmissions, draftStorage, draftSource, layer, layerType, isWindowLayer, isFramingAssembly]);

    const resetEmbC = useCallback(() => {
        if (!change) return;
        change(embPath, null);
        if (legacyCavityEmbPath) {
            change(legacyCavityEmbPath, null);
        }
    }, [change, embPath, legacyCavityEmbPath]);

    const mismatchMessage = isWindowLayer
        ? "Window selections changed since this EPD data was saved. Review or reset."
        : isFramingAssembly
          ? "Layer inputs changed since this EPD data was saved. Review or reset."
          : "Material selection changed since this EPD data was saved. Review or reset.";

    return (
        <div className={`${classes.editorPanel}`} onClick={(e) => e.stopPropagation()}>
            {display.hasMismatch && <div className={classes.mismatchNotice}>{mismatchMessage}</div>}
            <div className={classes.editorRow}>
                <div className={classes.editorRowInputs}>
                    <label className={classes.miniLabel}>
                        Emissions (kgCO₂/m²)
                        <input
                            className={classes.miniInput}
                            type="text"
                            inputMode="decimal"
                            value={draftEmissions}
                            onChange={(ev) => setDraftEmissions(ev.target.value)}
                        />
                    </label>
                    <label className={classes.miniLabel}>
                        Storage (kgCO₂/m²)
                        <input
                            className={classes.miniInput}
                            type="text"
                            inputMode="decimal"
                            value={draftStorage}
                            onChange={(ev) => setDraftStorage(ev.target.value)}
                        />
                    </label>
                    <button type="button" className={classes.resetBtn} title="Reset to defaults" onClick={resetEmbC}>
                        <span className={classes.resetBtnIcon} aria-hidden="true">
                            ↺
                        </span>
                    </button>
                </div>
                <button type="button" className={classes.applyBtn} onClick={applyEmbC}>
                    Apply
                </button>
            </div>
            <div className={classes.sourceRow}>
                <label className={classes.sourceLabel} htmlFor={sourceInputId}>
                    Source
                </label>
                <input
                    className={`${classes.sourceInput} keeper-ignore`}
                    type="text"
                    name="voltaEmbWindowOverallCitation"
                    id={sourceInputId}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                    placeholder={embC ? USER_DEFINED_LABEL : defaultSourceText || "—"}
                    value={draftSource}
                    onChange={(ev) => setDraftSource(sanitizeSourceInput(ev.target.value))}
                />
            </div>
        </div>
    );
});

export default EmbodiedCarbonEditor;
